/**
 * Subtitle Editor Page
 * Allows creators to edit transcribed subtitles before proceeding to parsing
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WaveformPlayer from '../components/subtitle-editor/WaveformPlayer';
import PlayerControls from '../components/subtitle-editor/PlayerControls';
import SubtitleList from '../components/subtitle-editor/SubtitleList';
import AccessDenied from '../components/AccessDenied';
import { creatorWorkflowService } from '../services/creatorWorkflowService';
import './SubtitleEditorPage.css';

interface Subtitle {
  index: number;
  text: string;
  startTime: number;
  endTime: number;
  words?: Array<{
    word: string;
    start: number;
    end: number;
    confidence?: number;
  }>;
}

const SubtitleEditorPage: React.FC = () => {
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['subtitle', 'common']);

  // Data state
  const [subtitles, setSubtitles] = useState<Subtitle[]>([]);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [accessDeniedReason, setAccessDeniedReason] = useState<'role' | 'ownership' | 'unknown'>('unknown');
  const [saving, setSaving] = useState(false);
  const [proceedingToParsing, setProceedingToParsing] = useState(false);

  // "Edit a published course" mode: when this draft is a shadow clone of a
  // live Course (set on the backend by start-edit), the bottom CTAs swap to
  // "Republish" + "Cancel edit" instead of the regular wizard advance flow.
  const [editingCourseId, setEditingCourseId] = useState<string | null>(null);
  const [republishing, setRepublishing] = useState(false);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [selectedSubtitleIndex, setSelectedSubtitleIndex] = useState<number | null>(null);

  // Auto-save timer
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdatesRef = useRef<Map<number, any>>(new Map());

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetIndex, setDeleteTargetIndex] = useState<number | null>(null);

  // Waveform player ref
  const waveformPlayerRef = useRef<any>(null);

  // Performance: Throttle time updates to prevent excessive re-renders
  const lastTimeUpdateRef = useRef<number>(0);
  const internalTimeRef = useRef<number>(0); // Track time without causing re-renders
  const TIME_UPDATE_THROTTLE_MS = 100; // Update UI at most 10 times per second

  // Load subtitles on mount
  useEffect(() => {
    if (!draftId) {
      setError(t('subtitle:editor.noDraftId'));
      setLoading(false);
      return;
    }

    loadSubtitles();
    // loadSubtitles is a plain function that only depends on draftId (already in deps);
    // t is stable from react-i18next
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  const loadSubtitles = async () => {
    if (!draftId) return;

    setLoading(true);
    setError('');
    setIsAccessDenied(false);
    setAccessDeniedReason('unknown');

    try {
      // Fetch subtitles + the draft itself in parallel — the draft tells us
      // whether this is a shadow edit of a published course (governs CTAs).
      const [subtitleData, draftData] = await Promise.all([
        creatorWorkflowService.getSubtitles(draftId),
        creatorWorkflowService.getDraft(draftId).catch(() => null),
      ]);

      setSubtitles(subtitleData.subtitles);
      setAudioUrl(subtitleData.audioUrl);
      setAudioDuration(subtitleData.audioDuration);
      setEditingCourseId(draftData?.editing_published_course_id ?? null);
    } catch (err: any) {
      console.error('Failed to load subtitles:', err);
      // Check for 403 Forbidden error
      if (err.response?.status === 403 || err.message?.includes('403')) {
        setIsAccessDenied(true);
        // Determine if it's an ownership issue or role issue
        const errorMessage = err.response?.data?.message || err.message || '';
        if (errorMessage.includes('do not have access') || errorMessage.includes('not belong')) {
          setAccessDeniedReason('ownership');
        } else {
          setAccessDeniedReason('role');
        }
        return;
      }
      setError(err.response?.data?.message || t('subtitle:editor.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to merge pending updates (instead of replacing)
  const queuePendingUpdate = (index: number, update: Record<string, any>) => {
    const existing = pendingUpdatesRef.current.get(index) || {};
    pendingUpdatesRef.current.set(index, { ...existing, ...update });
  };

  // Adjust pending updates after a subtitle is deleted
  // - Remove the deleted index
  // - Decrement all indexes > deletedIndex
  const adjustPendingUpdatesAfterDelete = (deletedIndex: number) => {
    const newPending = new Map<number, any>();
    pendingUpdatesRef.current.forEach((value, key) => {
      if (key < deletedIndex) {
        // Keep indexes before deleted unchanged
        newPending.set(key, value);
      } else if (key > deletedIndex) {
        // Decrement indexes after deleted
        newPending.set(key - 1, value);
      }
      // Skip the deleted index (key === deletedIndex)
    });
    pendingUpdatesRef.current = newPending;
  };

  // Adjust pending updates after a subtitle is added
  // - Increment all indexes >= insertedIndex
  const adjustPendingUpdatesAfterAdd = (insertedIndex: number) => {
    const newPending = new Map<number, any>();
    pendingUpdatesRef.current.forEach((value, key) => {
      if (key < insertedIndex) {
        // Keep indexes before inserted unchanged
        newPending.set(key, value);
      } else {
        // Increment indexes at or after inserted position
        newPending.set(key + 1, value);
      }
    });
    pendingUpdatesRef.current = newPending;
  };

  // Adjust pending updates after a subtitle is split
  // - Remove the split index (it's being replaced with new content)
  // - Increment all indexes > splitIndex by 1
  const adjustPendingUpdatesAfterSplit = (splitIndex: number) => {
    const newPending = new Map<number, any>();
    pendingUpdatesRef.current.forEach((value, key) => {
      if (key < splitIndex) {
        // Keep indexes before split unchanged
        newPending.set(key, value);
      } else if (key === splitIndex) {
        // Skip the split index (it's being replaced)
      } else {
        // Increment indexes after split
        newPending.set(key + 1, value);
      }
    });
    pendingUpdatesRef.current = newPending;
  };

  // Handle subtitle text change (debounced auto-save)
  const handleSubtitleTextChange = useCallback((index: number, newText: string) => {
    // Update local state immediately (optimistic update)
    setSubtitles((prev) =>
      prev.map((sub) => (sub.index === index ? { ...sub, text: newText } : sub))
    );

    // Queue update for auto-save (merge with existing pending updates)
    queuePendingUpdate(index, { text: newText });

    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    // Set new timer for auto-save (debounced 500ms)
    saveTimerRef.current = setTimeout(() => {
      saveAllPendingUpdates();
    }, 500);
    // saveAllPendingUpdates is a plain function that only depends on draftId (already in deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  // Handle subtitle timing change (from waveform region drag)
  const handleSubtitleTimingChange = useCallback((index: number, startTime: number, endTime: number) => {
    // Update local state immediately
    setSubtitles((prev) =>
      prev.map((sub) => (sub.index === index ? { ...sub, startTime, endTime } : sub))
    );

    // Queue update for auto-save (merge with existing pending updates)
    queuePendingUpdate(index, { startTime, endTime });

    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    // Set new timer for auto-save
    saveTimerRef.current = setTimeout(() => {
      saveAllPendingUpdates();
    }, 500);
    // saveAllPendingUpdates is a plain function that only depends on draftId (already in deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  // Save all pending updates to server
  const saveAllPendingUpdates = async () => {
    if (!draftId || pendingUpdatesRef.current.size === 0) return;

    setSaving(true);
    setError(''); // Clear previous errors

    try {
      // Save each pending update
      const updates = Array.from(pendingUpdatesRef.current.entries());
      const results = await Promise.allSettled(
        updates.map(([index, data]) =>
          creatorWorkflowService.updateSubtitle(draftId, index, data)
        )
      );

      // Check for failures
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        console.error('Some subtitle updates failed:', failures);
        setError(t('subtitle:editor.saveFailed', { count: failures.length }));
        // Keep only failed updates in pending (remove successful ones)
        const successfulIndices = updates
          .filter((_, i) => results[i].status === 'fulfilled')
          .map(([index]) => index);
        successfulIndices.forEach((index) => {
          pendingUpdatesRef.current.delete(index);
        });
      } else {
        // All successful - clear pending updates
        pendingUpdatesRef.current.clear();
      }
    } catch (err: any) {
      console.error('Failed to save subtitle updates:', err);
      setError(err.response?.data?.message || t('subtitle:editor.saveChangesFailed'));
      // On error, keep the pending updates for retry
    } finally {
      setSaving(false);
    }
  };

  // Handle play state change from WaveformPlayer
  const handlePlayStateChange = useCallback((playing: boolean) => {
    setIsPlaying(playing);
  }, []);

  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    if (waveformPlayerRef.current) {
      if (isPlaying) {
        waveformPlayerRef.current.pause();
      } else {
        waveformPlayerRef.current.play();
      }
      // State will be updated by onPlayStateChange callback
    }
  }, [isPlaying]);

  // Handle seek
  const handleSeek = useCallback((time: number) => {
    if (waveformPlayerRef.current) {
      waveformPlayerRef.current.seekTo(time);
      setCurrentTime(time);
    }
  }, []);

  // Handle subtitle selection
  const handleSubtitleSelect = useCallback((index: number) => {
    setSelectedSubtitleIndex(index);
    const subtitle = subtitles.find((s) => s.index === index);
    if (subtitle && waveformPlayerRef.current) {
      waveformPlayerRef.current.seekTo(subtitle.startTime);
      setCurrentTime(subtitle.startTime);
    }
  }, [subtitles]);

  // Handle play single subtitle segment
  const handlePlaySegment = useCallback((index: number) => {
    const subtitle = subtitles.find((s) => s.index === index);
    if (subtitle && waveformPlayerRef.current) {
      waveformPlayerRef.current.playSegment(subtitle.startTime, subtitle.endTime);
      setSelectedSubtitleIndex(index);
    }
  }, [subtitles]);

  // Handle region click from waveform
  const handleRegionClick = useCallback((index: number) => {
    setSelectedSubtitleIndex(index);
    // Scroll subtitle list to selected item
    const element = document.querySelector(`[data-subtitle-index="${index}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  // Handle splitting a subtitle at a specific time
  const handleSplitSubtitle = useCallback(async (index: number, splitTime: number) => {
    if (!draftId) return;

    const subtitleToSplit = subtitles.find((s) => s.index === index);
    if (!subtitleToSplit) return;

    // Validate split time is within the subtitle's time range
    if (splitTime <= subtitleToSplit.startTime || splitTime >= subtitleToSplit.endTime) {
      console.warn('Split time is outside subtitle range');
      return;
    }

    const originalText = subtitleToSplit.text;
    const actualSplitTime = splitTime;

    // Linear interpolation to find approximate split point
    const textWords = originalText.split(/\s+/);
    const totalDuration = subtitleToSplit.endTime - subtitleToSplit.startTime;
    const splitPosition = (splitTime - subtitleToSplit.startTime) / totalDuration;
    let splitWordIndex = Math.round(textWords.length * splitPosition);

    // Ensure splitWordIndex is within valid range
    splitWordIndex = Math.max(1, Math.min(splitWordIndex, textWords.length - 1));

    // IMPROVED: Search ±3 words for sentence-ending punctuation
    // Matches: . ! ? ." ?" !" .' ?' !' (handles dialogue endings)
    const SENTENCE_END_REGEX = /[.!?]["']?$/;
    const SEARCH_RANGE = 3;

    let bestSplitIndex = splitWordIndex;
    let foundSentenceEnd = false;

    // Search from closest to farthest within range
    for (let offset = 0; offset <= SEARCH_RANGE; offset++) {
      // Check before (splitWordIndex - offset - 1, because we split AFTER that word)
      const beforeIdx = splitWordIndex - offset;
      if (beforeIdx >= 1 && beforeIdx <= textWords.length - 1) {
        if (SENTENCE_END_REGEX.test(textWords[beforeIdx - 1])) {
          bestSplitIndex = beforeIdx;
          foundSentenceEnd = true;
          console.log(`Found sentence end at word "${textWords[beforeIdx - 1]}" (${offset} words before)`);
          break;
        }
      }

      // Check after
      const afterIdx = splitWordIndex + offset;
      if (afterIdx >= 1 && afterIdx <= textWords.length - 1 && offset > 0) {
        if (SENTENCE_END_REGEX.test(textWords[afterIdx - 1])) {
          bestSplitIndex = afterIdx;
          foundSentenceEnd = true;
          console.log(`Found sentence end at word "${textWords[afterIdx - 1]}" (${offset} words after)`);
          break;
        }
      }
    }

    if (!foundSentenceEnd) {
      console.log(`No sentence boundary found within ±${SEARCH_RANGE} words, using interpolated position`);
    }

    const firstHalfText = textWords.slice(0, bestSplitIndex).join(' ');
    const secondHalfText = textWords.slice(bestSplitIndex).join(' ');

    console.log(`Split result: "${firstHalfText}" | "${secondHalfText}"`);

    // CRITICAL: Adjust pending updates BEFORE any state changes
    // This fixes the race condition where auto-save could use stale indexes
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    adjustPendingUpdatesAfterSplit(index);

    // Optimistic update: update local state immediately
    setSubtitles((prev) => {
      const newSubtitles = [...prev];
      const targetIndex = newSubtitles.findIndex((s) => s.index === index);
      if (targetIndex === -1) return prev;

      const original = newSubtitles[targetIndex];

      // Create two new subtitles from the split (use actualSplitTime for precise timing)
      const firstHalf: Subtitle = {
        index: original.index,
        text: firstHalfText,
        startTime: original.startTime,
        endTime: actualSplitTime,
      };

      const secondHalf: Subtitle = {
        index: original.index + 1,
        text: secondHalfText,
        startTime: actualSplitTime,
        endTime: original.endTime,
      };

      // Replace the original with first half, insert second half after
      newSubtitles.splice(targetIndex, 1, firstHalf, secondHalf);

      // Re-index all subtitles after the split point
      const reindexed = newSubtitles.map((sub, idx) => ({
        ...sub,
        index: idx,
      }));

      return reindexed;
    });

    // Select the first part of the split
    setSelectedSubtitleIndex(index);

    // Call backend API to persist the split (use actualSplitTime for precise timing)
    setSaving(true);
    try {
      const result = await creatorWorkflowService.splitSubtitle(
        draftId,
        index,
        actualSplitTime,
        firstHalfText,
        secondHalfText,
      );

      // Update local state with backend response (ensures consistency)
      if (result.success && result.subtitles) {
        setSubtitles(result.subtitles.map((s) => ({
          index: s.index,
          text: s.text,
          startTime: s.startTime,
          endTime: s.endTime,
        })));
      }

      // After successful split, save any remaining pending updates with adjusted indexes
      if (pendingUpdatesRef.current.size > 0) {
        await saveAllPendingUpdates();
      }
    } catch (err: any) {
      console.error('Failed to split subtitle:', err);
      setError(err.response?.data?.message || t('subtitle:editor.splitFailed'));
      // Reload subtitles to revert optimistic update on error
      loadSubtitles();
    } finally {
      setSaving(false);
    }
    // saveAllPendingUpdates, loadSubtitles, and t have circular deps with subtitles state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, subtitles]);

  // Handle adding new subtitle
  const handleAddSubtitle = useCallback(async (afterIndex: number | null) => {
    if (!draftId) return;

    // Calculate timing for the new subtitle
    let newStartTime: number;
    let newEndTime: number;

    if (afterIndex !== null) {
      const currentSub = subtitles.find(s => s.index === afterIndex);
      const nextSub = subtitles.find(s => s.index === afterIndex + 1);

      if (currentSub) {
        newStartTime = currentSub.endTime;
        newEndTime = nextSub ? nextSub.startTime : currentSub.endTime + 2;
      } else {
        const lastSub = subtitles[subtitles.length - 1];
        newStartTime = lastSub ? lastSub.endTime : 0;
        newEndTime = newStartTime + 2;
      }
    } else {
      const lastSub = subtitles[subtitles.length - 1];
      newStartTime = lastSub ? lastSub.endTime : 0;
      newEndTime = newStartTime + 2;
    }

    const newSubtitleData = {
      text: t('subtitle:editor.newSubtitleText'),
      startTime: newStartTime,
      endTime: newEndTime,
    };

    // Calculate insert position
    const insertPosition = afterIndex !== null ? afterIndex + 1 : subtitles.length;

    // CRITICAL: Adjust pending updates BEFORE any state changes
    // This fixes the race condition where auto-save could use stale indexes
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    adjustPendingUpdatesAfterAdd(insertPosition);

    // Optimistic update: add to local state immediately
    setSubtitles((prev) => {
      const newSubtitles = [...prev];
      const newSubtitle: Subtitle = {
        index: insertPosition,
        ...newSubtitleData,
      };
      newSubtitles.splice(insertPosition, 0, newSubtitle);
      // Re-index all subtitles
      return newSubtitles.map((sub, idx) => ({ ...sub, index: idx }));
    });

    // Select the newly added subtitle
    setSelectedSubtitleIndex(insertPosition);

    // Call backend API to persist the new subtitle
    setSaving(true);
    try {
      const result = await creatorWorkflowService.addSubtitle(draftId, afterIndex, newSubtitleData);

      // Update local state with backend response (ensures consistency)
      if (result.success && result.subtitles) {
        setSubtitles(result.subtitles.map((s) => ({
          index: s.index,
          text: s.text,
          startTime: s.startTime,
          endTime: s.endTime,
        })));
        setSelectedSubtitleIndex(result.newIndex);
      }

      // After successful add, save any remaining pending updates with adjusted indexes
      if (pendingUpdatesRef.current.size > 0) {
        await saveAllPendingUpdates();
      }
    } catch (err: any) {
      console.error('Failed to add subtitle:', err);
      setError(err.response?.data?.message || t('subtitle:editor.addFailed'));
      // Reload subtitles to revert optimistic update on error
      loadSubtitles();
    } finally {
      setSaving(false);
    }
    // saveAllPendingUpdates, loadSubtitles, and t have circular deps with subtitles state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, subtitles]);

  // Handle deleting a subtitle - opens confirmation modal
  const handleDeleteSubtitle = useCallback((index: number) => {
    setDeleteTargetIndex(index);
    setDeleteModalOpen(true);
  }, []);

  // Cancel delete operation
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpen(false);
    setDeleteTargetIndex(null);
  }, []);

  // Confirm and perform deletion
  const handleConfirmDelete = useCallback(async () => {
    if (!draftId || deleteTargetIndex === null) return;

    const index = deleteTargetIndex;

    // Close modal first
    setDeleteModalOpen(false);
    setDeleteTargetIndex(null);

    // CRITICAL: Adjust pending updates BEFORE any state changes
    // This fixes the race condition where auto-save could use stale indexes
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    adjustPendingUpdatesAfterDelete(index);

    // Optimistic update: remove from local state immediately
    setSubtitles((prev) => {
      const newSubtitles = prev.filter((s) => s.index !== index);
      // Re-index all subtitles
      return newSubtitles.map((sub, idx) => ({
        ...sub,
        index: idx,
      }));
    });

    // Clear selection if deleted subtitle was selected
    if (selectedSubtitleIndex === index) {
      setSelectedSubtitleIndex(null);
    } else if (selectedSubtitleIndex !== null && selectedSubtitleIndex > index) {
      // Adjust selection index if it was after the deleted item
      setSelectedSubtitleIndex(selectedSubtitleIndex - 1);
    }

    // Call backend API to persist the deletion
    setSaving(true);
    try {
      await creatorWorkflowService.deleteSubtitle(draftId, index);

      // After successful delete, save any remaining pending updates with adjusted indexes
      if (pendingUpdatesRef.current.size > 0) {
        await saveAllPendingUpdates();
      }
    } catch (err: any) {
      console.error('Failed to delete subtitle:', err);
      setError(err.response?.data?.message || t('subtitle:editor.deleteFailed'));
      // Reload subtitles to revert optimistic update on error
      loadSubtitles();
    } finally {
      setSaving(false);
    }
    // saveAllPendingUpdates, loadSubtitles, and t have circular deps with component state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, deleteTargetIndex, selectedSubtitleIndex]);

  // Handle time update from waveform (THROTTLED for performance)
  // This is called on every audio frame (~60fps) - we throttle to prevent excessive re-renders
  const handleTimeUpdate = useCallback((time: number) => {
    // Always update internal ref (no re-render)
    internalTimeRef.current = time;

    const now = Date.now();
    const shouldUpdateUI = now - lastTimeUpdateRef.current >= TIME_UPDATE_THROTTLE_MS;

    // Find current subtitle based on time (use internal ref)
    // Use a small epsilon for boundary comparisons to handle floating point precision issues
    const EPSILON = 0.001; // 1ms tolerance
    const currentSubtitle = subtitles.find(
      (s) => time >= s.startTime - EPSILON && time < s.endTime - EPSILON
    );
    const newSubtitleIndex = currentSubtitle ? currentSubtitle.index : null;

    // Only auto-update selection during playback (when isPlaying is true)
    // Don't override user's manual selection when seeking
    if (isPlaying && newSubtitleIndex !== null && newSubtitleIndex !== selectedSubtitleIndex) {
      setSelectedSubtitleIndex(newSubtitleIndex);
    }

    // Throttle currentTime state updates to reduce re-renders
    if (shouldUpdateUI) {
      lastTimeUpdateRef.current = now;
      setCurrentTime(time);
    }
  }, [subtitles, selectedSubtitleIndex, isPlaying]);

  // Handle proceed to parsing
  const handleProceedToParsing = async () => {
    if (!draftId) return;

    // Save any pending updates first
    if (pendingUpdatesRef.current.size > 0) {
      await saveAllPendingUpdates();
    }

    setProceedingToParsing(true);
    try {
      await creatorWorkflowService.proceedToParsing(draftId);
      // Navigate back to wizard page
      navigate(`/creator/wizard/${draftId}`);
    } catch (err: any) {
      console.error('Failed to proceed to parsing:', err);
      setError(err.response?.data?.message || t('subtitle:editor.proceedFailed'));
      setProceedingToParsing(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (draftId) {
      navigate(`/creator/wizard/${draftId}`);
    } else {
      navigate('/creator/wizard');
    }
  };

  // ── Edit-mode actions (only fire when editingCourseId is set) ──────

  // Atomically swap the shadow draft's content back into the live course.
  // Service handles segment by-id update vs structural replacement, learner
  // ref remap, and shadow-draft cleanup in a single transaction.
  const handleRepublish = async () => {
    if (!draftId) return;
    const confirmed = window.confirm(t('subtitle:editor.republishConfirm'));
    if (!confirmed) return;

    if (pendingUpdatesRef.current.size > 0) {
      await saveAllPendingUpdates();
    }
    setRepublishing(true);
    try {
      const { courseId } = await creatorWorkflowService.republishDraft(draftId);
      // Land back on the public course page so creator can see the live result
      navigate(`/courses/${courseId}`);
    } catch (err: any) {
      console.error('Republish failed:', err);
      setError(err.response?.data?.message || t('subtitle:editor.republishFailed'));
      setRepublishing(false);
    }
  };

  // Re-run AI annotation for one sentence. Only meaningful in edit-mode (the
  // shadow draft was hydrated with annotations from the live course; if the
  // creator changes text those annotations need refreshing).
  const handleReparseSentence = useCallback(async (index: number) => {
    if (!draftId) return;
    if (pendingUpdatesRef.current.size > 0) {
      // Push pending text/timing changes first so the parser sees the new text.
      await saveAllPendingUpdates();
    }
    try {
      await creatorWorkflowService.reparseSentence(draftId, index);
    } catch (err: any) {
      console.error('Reparse failed:', err);
      alert(err.response?.data?.message || t('subtitle:editor.reparseFailed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, t]);

  // Drop the shadow draft entirely; live course is untouched. Used when the
  // creator decides their edits aren't worth shipping.
  const handleCancelEdit = async () => {
    if (!draftId) return;
    const confirmed = window.confirm(t('subtitle:editor.cancelEditConfirm'));
    if (!confirmed) return;
    try {
      await creatorWorkflowService.cancelEditPublishedCourse(draftId);
      // Back to creator courses list — no draft to return to anymore.
      navigate('/creator/courses?tab=published');
    } catch (err: any) {
      console.error('Cancel edit failed:', err);
      setError(err.response?.data?.message || t('subtitle:editor.cancelEditFailed'));
    }
  };


  if (loading) {
    return (
      <div className="subtitle-editor-page loading">
        <div className="subtitle-editor-loading">
          <span className="subtitle-editor-loading-text">{t('subtitle:editor.loading')}</span>
        </div>
      </div>
    );
  }

  // Show access denied message
  if (isAccessDenied) {
    return (
      <div className="subtitle-editor-page">
        <AccessDenied reason={accessDeniedReason} />
      </div>
    );
  }

  if (error && !subtitles.length) {
    return (
      <div className="subtitle-editor-page error">
        <div className="error-message">
          <h2>{t('subtitle:editor.error')}</h2>
          <p>{error}</p>
          <button onClick={handleBack} className="btn-secondary">
            {t('subtitle:editor.goBack')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="subtitle-editor-page">
      {/* Edit-mode banner — visible only when editing a live course */}
      {editingCourseId && (
        <div className="edit-mode-banner" role="status" aria-live="polite">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 9v4M12 17h.01" />
            <circle cx="12" cy="12" r="10" />
          </svg>
          <span>{t('subtitle:editor.editingPublishedCourseBanner')}</span>
        </div>
      )}

      {/* Combined Header with Controls */}
      <header className="editor-header">
        <h1>{editingCourseId ? t('subtitle:editor.titleEditing') : t('subtitle:editor.title')}</h1>
        <div className="header-controls">
          <PlayerControls
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={audioDuration}
            onPlayPause={handlePlayPause}
            onSeek={handleSeek}
          />
        </div>
        <div className="header-actions">
          {saving && <span className="saving-indicator">{t('subtitle:editor.saving')}</span>}
          {error && <span className="error-indicator">{error}</span>}
        </div>
      </header>

      <div className="editor-content">
        {/* Waveform Section */}
        <div className="sticky-player-section">
          <div className="waveform-section">
            <WaveformPlayer
              ref={waveformPlayerRef}
              audioUrl={audioUrl}
              subtitles={subtitles}
              selectedIndex={selectedSubtitleIndex}
              onRegionClick={handleRegionClick}
              onRegionUpdate={handleSubtitleTimingChange}
              onTimeUpdate={handleTimeUpdate}
              onPlayStateChange={handlePlayStateChange}
              onSplitSubtitle={handleSplitSubtitle}
            />
          </div>
        </div>

        {/* Subtitle List */}
        <div className="subtitle-list-section">
          <SubtitleList
            subtitles={subtitles}
            selectedIndex={selectedSubtitleIndex}
            currentTime={currentTime}
            onSubtitleSelect={handleSubtitleSelect}
            onSubtitleTextChange={handleSubtitleTextChange}
            onPlaySegment={handlePlaySegment}
            onAddSubtitle={handleAddSubtitle}
            onDeleteSubtitle={handleDeleteSubtitle}
            onReparseSentence={editingCourseId ? handleReparseSentence : undefined}
          />
        </div>
      </div>

      {/* Bottom Actions — CTAs differ for "edit a published course" mode */}
      <div className="editor-actions">
        {editingCourseId ? (
          <>
            <button onClick={handleCancelEdit} className="btn-secondary" disabled={republishing}>
              {t('subtitle:editor.cancelEdit')}
            </button>
            <button
              onClick={handleRepublish}
              className="btn-primary"
              disabled={republishing || saving}
            >
              {republishing ? t('subtitle:editor.republishing') : t('subtitle:editor.republish')}
            </button>
          </>
        ) : (
          <>
            <button onClick={handleBack} className="btn-secondary">
              {t('subtitle:editor.back')}
            </button>
            <button
              onClick={handleProceedToParsing}
              className="btn-primary"
              disabled={proceedingToParsing || saving}
            >
              {proceedingToParsing ? t('subtitle:editor.processing') : t('subtitle:editor.proceedToParsing')}
            </button>
          </>
        )}
      </div>

      {/* Processing Overlay (parsing or republish) */}
      {(proceedingToParsing || republishing) && (
        <div className="processing-overlay">
          <div className="processing-content">
            <div className="processing-spinner"></div>
            <h3>
              {republishing
                ? t('subtitle:editor.republishingTitle')
                : t('subtitle:editor.processingTitle')}
            </h3>
            <p>
              {republishing
                ? t('subtitle:editor.republishingDescription')
                : t('subtitle:editor.processingDescription')}
            </p>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && deleteTargetIndex !== null && (
        <div className="delete-modal-overlay" onClick={handleCancelDelete}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="#ff6b6b">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
              </svg>
              <h2>{t('subtitle:deleteModal.title')}</h2>
            </div>
            <div className="delete-modal-body">
              <p>{t('subtitle:deleteModal.message', { index: deleteTargetIndex + 1 })}</p>
              <div className="delete-modal-preview">
                "{subtitles.find(s => s.index === deleteTargetIndex)?.text.substring(0, 80)}
                {(subtitles.find(s => s.index === deleteTargetIndex)?.text.length || 0) > 80 ? '...' : ''}"
              </div>
              <p className="delete-modal-warning">{t('subtitle:deleteModal.warning')}</p>
            </div>
            <div className="delete-modal-actions">
              <button className="btn-secondary" onClick={handleCancelDelete}>
                {t('common:cancel')}
              </button>
              <button className="btn-danger" onClick={handleConfirmDelete}>
                {t('common:delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubtitleEditorPage;
